import type { Interval } from "$apis/parkito2/index.ts";

export default class IntervalModel implements Interval {
  public _data: Interval;
  constructor(data: Interval) {
    this._data = data;
  }
  static fromMillis(millis: number) {
    return new IntervalModel({
      micros: millis * 1000000,
      months: 0,
      days: 0,
    });
  }
  static fromMinutes(minutes: number) {
    return new IntervalModel({
      micros: minutes * 60 * 1000000,
      months: 0,
      days: 0,
    });
  }
  static fromMonths(months: number) {
    return new IntervalModel({
      micros: 0,
      months,
      days: 0,
    });
  }
  static fromDays(days: number) {
    return new IntervalModel({
      micros: 0,
      months: 0,
      days,
    });
  }

  get days() {
    return this._data.days;
  }
  get micros() {
    return this._data.micros;
  }
  get months() {
    return this._data.months;
  }

  /**
   * Format interval into human readable string.
   *
   * options.implicitMultiplier - default false. Set to true if you dont want to print
   *                              the initial multiplier in when it is one,
   *                              like in "50kr / `månad`" vs "50 kr / `1 månad`"
   *                              It also affects the interval when the multiplier is 2.
   *                              When true then "2 månader" will become "varannan månad".
   */
  format(options?: { implicitMultiplier?: boolean }): string | undefined {
    const years = Math.floor(this.months / 12);
    const months = this.months - (12 * years);

    //const weeks = Math.floor(this.days / 7);
    const days = this.days; // - (7 * weeks);

    const microsInSecond = 1000000;
    const microsInMinute = microsInSecond * 60;
    const microsInHour = microsInMinute * 60;
    let microsLeft = this.micros;
    const hours = Math.floor(microsLeft / microsInHour);
    microsLeft = microsLeft - (hours * microsInHour);
    const minutes = Math.floor(microsLeft / microsInMinute);
    microsLeft = microsLeft - (minutes * microsInMinute);
    const seconds = Math.floor(microsLeft / microsInSecond);
    const micros = microsLeft - (seconds * microsInSecond);

    const unitSpecs: Array<[number, FormatCountOptions]> = [
      [years, {
        unit: "år",
        unitPlural: "år",
        unitType: "ett",
        implicitMultiplier: false,
      }],
      [months, {
        unit: "månad",
        unitPlural: "månader",
        unitType: "en",
        implicitMultiplier: false,
      }],
      /*[weeks, {
        unit: "vecka",
        unitPlural: "veckor",
        unitType: "en",
        implicitMultiplier: false,
      }],*/
      [days, {
        unit: "dag",
        unitPlural: "dagar",
        unitType: "en",
        implicitMultiplier: false,
      }],
      [hours, {
        unit: "timme",
        unitPlural: "timmar",
        unitType: "en",
        implicitMultiplier: false,
      }],
      [minutes, {
        unit: "minut",
        unitPlural: "minuter",
        unitType: "en",
        implicitMultiplier: false,
      }],
      [seconds, {
        unit: "sekund",
        unitPlural: "sekunder",
        unitType: "en",
        implicitMultiplier: false,
      }],
      [micros, {
        unit: "mikrosekund",
        unitPlural: "mikrosekunder",
        unitType: "en",
        implicitMultiplier: false,
      }],
    ];

    const nonZeroUnitSpecs = unitSpecs.filter(([num, _]) => num > 0);

    if (nonZeroUnitSpecs.length === 0) {
      return undefined;
    }

    nonZeroUnitSpecs[0][1].implicitMultiplier = options?.implicitMultiplier ??
      false;

    const formatted = nonZeroUnitSpecs.map(([num, spec]) =>
      formatCount(num, spec)
    );
    if (formatted.length === 1) {
      return formatted[0];
    }

    const last = formatted.pop();

    return formatted.join(", ") + " och " + last;
  }
}

type FormatCountOptions = {
  unit: string;
  unitPlural: string;
  unitType: "en" | "ett";
  implicitMultiplier: boolean;
};
function formatCount(
  num: number,
  options: FormatCountOptions,
) {
  if (num === 0) {
    return undefined;
  }
  if (num === 1) {
    if (options.implicitMultiplier) {
      return options.unit;
    } else {
      return options.unitType + " " + options.unit;
    }
  }

  return `${num} ${options.unitPlural}`;
}
